import React from 'react';
import { motion } from 'framer-motion';
import { FileText, ClipboardList, AlertTriangle, Calendar, Stethoscope, Activity } from 'lucide-react';
export function BonusStack() {
  const bonuses = [{
    icon: ClipboardList,
    title: 'Checklist Clínico de Valoración Inicial',
    desc: 'Una guía práctica para no olvidarte de nada en el primer contacto con el paciente. Ideal para prácticas, internado y piso.',
    value: '$8.000'
  }, {
    icon: Activity,
    title: 'Tabla Maestra de Valores Normales y Signos de Alarma',
    desc: 'Tu referencia visual rápida para interpretar signos vitales, detectar desviaciones y actuar con más criterio.',
    value: '$10.000'
  }, {
    icon: FileText,
    title: 'Plantillas de Registros de Enfermería',
    desc: 'Modelos de evolución, pase de guardia, control de signos y eventos adversos para registrar mejor y con más seguridad.',
    value: '$9.000'
  }, {
    icon: Stethoscope,
    title: 'Guía Express de Medicación y Cálculos',
    desc: 'Resumen rápido para revisar dosis, goteo, diluciones, alto riesgo y los 10 correctos sin perder tiempo.',
    value: '$8.000'
  }, {
    icon: AlertTriangle,
    title: 'Guía de Red Flags y Deterioro Clínico',
    desc: 'Detectá las señales de alarma que no podés ignorar y sabé qué vigilar primero.',
    value: '$9.000'
  }, {
    icon: Calendar,
    title: 'Plan de Estudio Clínico de 21 Días',
    desc: 'Un sistema claro para estudiar el Mega Pack sin perderte, con orden, foco y seguimiento de progreso.',
    value: '$9.000'
  }];
  return <section className="py-12 md:py-20 lg:py-24 bg-clinical-base" data-id="element-20">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21">
        <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14" data-id="element-22">
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-[10px] sm:text-xs font-semibold tracking-[0.22em] uppercase text-clinical-mid mb-4" data-id="element-23">
            Bonos incluidos
          </motion.div>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl sm:text-4xl md:text-5xl font-black text-clinical-deep mb-4 leading-[1.05] uppercase" data-id="element-24">
            6 bonos{' '}
            <span className="accent-italic normal-case" data-id="element-25">estratégicos</span>
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-base md:text-lg text-clinical-ink" data-id="element-26">
            Siempre incluidos. Listos para usar desde{' '}
            <span className="accent-italic" data-id="element-27">el primer día.</span>
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 mb-10 md:mb-12" data-id="element-28">
          {bonuses.map((bonus, idx) => {
          const Icon = bonus.icon;
          return <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.04
          }} className="bg-white rounded-2xl p-5 md:p-6 border border-clinical-line flex flex-col h-full" data-id="element-29">
                <div className="flex items-center justify-between mb-4" data-id="element-30">
                  <div className="w-11 h-11 rounded-xl bg-clinical-base border border-clinical-line flex items-center justify-center" data-id="element-31">
                    <Icon className="w-5 h-5 text-clinical-mid" strokeWidth={2} data-id="element-32" />
                  </div>
                  <div className="px-2.5 py-1 bg-clinical-soft text-clinical-deep text-[10px] font-bold rounded-full tracking-wider" data-id="element-33">
                    BONO {String(idx + 1).padStart(2, '0')}
                  </div>
                </div>

                <h4 className="text-base md:text-lg font-bold text-clinical-deep mb-2 leading-snug uppercase" data-id="element-34">
                  {bonus.title}
                </h4>

                <p className="text-sm text-clinical-ink mb-5 flex-grow leading-relaxed" data-id="element-35">
                  {bonus.desc}
                </p>

                <div className="flex items-center justify-between pt-3 border-t border-clinical-line mt-auto" data-id="element-36">
                  <span className="text-clinical-ink/40 line-through text-sm font-medium" data-id="element-37">
                    {bonus.value}
                  </span>
                  <span className="bg-clinical-deep text-clinical-accent font-bold px-3 py-1 rounded-full text-xs tracking-wider" data-id="element-38">
                    GRATIS
                  </span>
                </div>
              </motion.div>;
        })}
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="max-w-3xl mx-auto bg-clinical-deep rounded-2xl md:rounded-3xl p-7 md:p-10 text-center" data-id="element-39">
          <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.22em] text-clinical-accent mb-3" data-id="element-40">
            Valor total de bonos
          </p>
          <p className="text-2xl md:text-3xl font-black text-white leading-tight uppercase" data-id="element-41">
            <span className="line-through text-white/40 font-bold" data-id="element-42">
              $53.000
            </span>
            <span className="mx-3 text-clinical-accent" data-id="element-43">→</span>
            <span data-id="element-44">Gratis</span>
          </p>
          <p className="text-sm md:text-base text-white/70 mt-2 normal-case" data-id="element-45">
            con tu Mega Pack Enfermería
          </p>
        </motion.div>
      </div>
    </section>;
}